// =============================================================================
// build-cost-service.ts — pure-function port of Services/BuildCostService.cs
// No class, no singleton, no module-level state.
//
// Unity BuildCostService is a MonoBehaviour singleton that reads from:
//   • FacilityRegistry (baseCost lookup) — web callers resolve this externally
//   • PendingEffectQueue (bumps + legacy PEQ discounts)
//   • AuraManager (BuildCost auras)
//   • CardInstanceService + CardDeckManager (hand scan for hand-driven path)
//
// Web API: pass state as arguments. Registry lookup removed — callers pass
// baseCost directly (resolved from FacilityStats outside this service).
// Hand-driven path uses CardInstanceState + cards array for scanning.
//
// Function map (Unity → TS):
//   GetEffectiveCost(seat, ft, terrain, era, previewDiscount?)  → getEffectiveCost(peqState, auraState, baseCost, seatIndex, ft, previewDiscount?)
//   GetEffectiveCost(seat, ft, terrain, era, PendingKind?)      → getEffectiveCostWithOverride(peqState, auraState, baseCost, seatIndex, ft, overrideDiscount)
//   ScanHandForBuildDiscounts(seat)                             → scanHandForBuildDiscounts(cisState, cards, seat) [pure, exported]
//   ApplicableHandDiscounts(seat, ft)                           → applicableHandDiscounts(cisState, cards, seat, ft) [pure, exported]
//   HasAnyHandBuildDiscount(seat)                               → hasAnyHandBuildDiscount(cisState, cards, seat)
//   GetEffectiveCostFromHand(seat, ft, terrain, era, chosenId)  → getEffectiveCostFromHand(cisState, cards, auraState, baseCost, seatIndex, ft, chosenInstanceId)
//   OnBuildExecuted(seat, ft, chosenKind?)                      → onBuildExecuted(peqState, log, seatIndex, ft, chosenDiscountKind?) → {newState, log}
//   OnBuildExecutedFromHand(seat, ft, chosenId)                 → onBuildExecutedFromHand(cisState, cards, log, seatIndex, ft, chosenInstanceId?) → {newState, log}
// =============================================================================

import type { FacilityCategory, FacilityType, PendingKind } from "@gdg/shared";
import type { CardDef } from "@gdg/shared";
import type { ActionLog, JsonValue } from "../rng.js";
import {
  FACILITY_CATEGORY_VALUES,
  FACILITY_TYPE_VALUES,
  tryParseEnumIgnoreCase,
} from "./_facility-enum.js";
import { assertFiniteInt } from "./_validation.js";
import type { HandDiscount } from "./aura-breakdown.js";
import { getByScope } from "./aura-manager.js";
import type { AuraManagerState } from "./aura-manager.js";
import { getByOwner, moveZone } from "./card-instance-service.js";
import type { CardInstanceState } from "./card-instance-service.js";
import { getCategory } from "./facility-classifier.js";
import { applyModifier } from "./modifier-math.js";
import {
  drainBumps,
  hasAnyDiscount,
  peekIncludingGlobal,
  sumActiveBumps,
  tryConsume,
} from "./pending-effect-queue.js";
import type { PendingEffectQueueState } from "./pending-effect-queue.js";

// Re-export HandDiscount so callers can import from build-cost-service or aura-breakdown.
export type { HandDiscount } from "./aura-breakdown.js";

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface BuildExecuteResult {
  readonly newState: PendingEffectQueueState;
  readonly log: ActionLog;
}

export interface BuildExecuteFromHandResult {
  readonly newState: CardInstanceState;
  readonly log: ActionLog;
}

// ---------------------------------------------------------------------------
// Internal filter helpers (mirrors BuildCostService.cs CategoryFits / FacilityFits)
// ---------------------------------------------------------------------------

function categoryFits(
  cat: FacilityCategory | null | undefined,
  filter: FacilityCategory | null | undefined,
): boolean {
  if (filter == null) return true;
  if (cat == null) return false;
  return filter === cat;
}

function facilityFits(
  ft: FacilityType | null | undefined,
  filter: FacilityType | null | undefined,
): boolean {
  if (filter == null) return true;
  if (ft == null) return false;
  return filter === ft;
}

// ---------------------------------------------------------------------------
// scanHandForBuildDiscounts (port of static ScanHandForBuildDiscounts)
// ---------------------------------------------------------------------------

/**
 * Scan a seat's Hand-zone cards for CardEffects of kind BuildCostMod with
 * discardMode = Whenever.
 *
 * Returns one HandDiscount per matching CardEffect.
 *
 * Mirrors BuildCostService.ScanHandForBuildDiscounts(seat).
 *
 * @param cisState - CardInstanceState (all card instances)
 * @param cards    - array of CardDef from cards.json (for looking up effects)
 * @param seat     - seat index to scan
 */
export function scanHandForBuildDiscounts(
  cisState: CardInstanceState,
  cards: ReadonlyArray<CardDef>,
  seat: number,
): ReadonlyArray<HandDiscount> {
  assertFiniteInt(seat, "scanHandForBuildDiscounts seat");

  const result: HandDiscount[] = [];
  const instances = getByOwner(cisState, seat);

  for (const inst of instances) {
    if (inst.zone !== "Hand") continue;

    // Find card definition by asset name (matches CardDeckManager.FindCardByName)
    const card = cards.find((c) => c.name === inst.cardAssetId);
    if (card == null) continue;
    if (card.discardMode !== "Whenever") continue;
    if (card.effects == null || card.effects.length === 0) continue;

    for (const eff of card.effects) {
      if (eff.kind !== "BuildCostMod") continue;
      if (eff.pendingKind == null) continue;

      // Parse categoryFilter (first entry): try FacilityCategory, then FacilityType
      let catFilter: FacilityCategory | null = null;
      let ftFilter: FacilityType | null = null;

      if (eff.categoryFilter != null && eff.categoryFilter.length > 0) {
        const raw = eff.categoryFilter[0];
        if (raw != null) {
          // Unity parity: Enum.TryParse(..., true, ...) — case-insensitive.
          // Shared helper (services/_facility-enum.ts) — parser drift fixed in
          // WEB.2.1 cross-group sweep (was case-sensitive includes(raw)).
          const parsedCat = tryParseEnumIgnoreCase(raw, FACILITY_CATEGORY_VALUES);
          if (parsedCat !== null) {
            catFilter = parsedCat;
          } else {
            const parsedFt = tryParseEnumIgnoreCase(raw, FACILITY_TYPE_VALUES);
            if (parsedFt !== null) ftFilter = parsedFt;
          }
        }
      }

      // Use card.cardName if non-empty, else card.name (mirrors Unity IsNullOrEmpty check)
      const displayName = card.cardName.length > 0 ? card.cardName : card.name;

      result.push({
        instanceId: inst.instanceId,
        sourceCardName: displayName,
        kind: eff.pendingKind,
        valueInt: eff.valueInt ?? 0,
        categoryFilter: catFilter,
        facilityFilter: ftFilter,
      });
    }
  }

  return Object.freeze(result);
}

// ---------------------------------------------------------------------------
// applicableHandDiscounts
// ---------------------------------------------------------------------------

/**
 * Filter hand discounts applicable to a specific (seat, ft) build.
 * Mirrors BuildCostService.ApplicableHandDiscounts(seat, ft).
 */
export function applicableHandDiscounts(
  cisState: CardInstanceState,
  cards: ReadonlyArray<CardDef>,
  seat: number,
  ft: FacilityType,
): ReadonlyArray<HandDiscount> {
  const all = scanHandForBuildDiscounts(cisState, cards, seat);
  const cat = getCategory(ft);
  const result: HandDiscount[] = [];
  for (const d of all) {
    if (d.categoryFilter != null && d.categoryFilter !== cat) continue;
    if (d.facilityFilter != null && d.facilityFilter !== ft) continue;
    result.push(d);
  }
  return Object.freeze(result);
}

// ---------------------------------------------------------------------------
// hasAnyHandBuildDiscount
// ---------------------------------------------------------------------------

/**
 * Quick check: does the seat's hand have ANY build-side pending card?
 * EXCLUDES NextPaidDrawFree (Silicon Valley = paid-draw, not build).
 *
 * Mirrors BuildCostService.HasAnyHandBuildDiscount(seat).
 */
export function hasAnyHandBuildDiscount(
  cisState: CardInstanceState,
  cards: ReadonlyArray<CardDef>,
  seat: number,
): boolean {
  const all = scanHandForBuildDiscounts(cisState, cards, seat);
  for (const d of all) {
    if (d.kind === "NextPaidDrawFree") continue;
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Internal stacking core (shared by getEffectiveCost* variants)
// ---------------------------------------------------------------------------

function stackAurasAndBumps(
  peqState: PendingEffectQueueState,
  auraState: AuraManagerState,
  baseCost: number,
  seatIndex: number,
  ft: FacilityType,
): { multAcc: number; addAcc: number; afterStack: number } {
  const cat = getCategory(ft);

  // Step 1: PEQ bumps (auto-apply, additive). Codex WEB.4.D R1 HIGH fix:
  // thread `ft` so type-filtered bumps (Drought Hydro) only apply to Hydro builds.
  const bumps = sumActiveBumps(peqState, seatIndex, cat, ft);

  // Step 2: BuildCost auras
  let multAcc = 1;
  let addAcc = bumps;

  for (const a of getByScope(auraState, "BuildCost", seatIndex)) {
    // WEB.4.1: honor facilityFilter (FacilityType-level) + categoryFilter.
    if (a.categoryFilter != null && a.categoryFilter !== cat) continue;
    if (a.facilityFilter != null && a.facilityFilter !== ft) continue;
    // WEB.4.1: mult=0 semantic fix (option c) — undefined=absent, 0=zero-override.
    const eMult = a.effect.mult !== undefined ? a.effect.mult : 1;
    const eAdd = a.effect.add ?? 0;
    multAcc *= eMult;
    addAcc += eAdd;
  }

  const { result: afterStack } = applyModifier(baseCost, multAcc, addAcc, "BuildCost");
  return { multAcc, addAcc, afterStack };
}

// ---------------------------------------------------------------------------
// getEffectiveCost (PEQ-path, preview-discount bool overload)
// ---------------------------------------------------------------------------

/**
 * Effective cost with optional best-case discount preview.
 * previewDiscount=true → returns best possible cost if player uses any discount.
 * previewDiscount=false (default) → stacking-only quote (no discount applied).
 *
 * Mirrors BuildCostService.GetEffectiveCost(seatIndex, ft, terrain, era, previewDiscount?).
 * Web: caller resolves baseCost from FacilityRegistry externally.
 */
export function getEffectiveCost(
  peqState: PendingEffectQueueState,
  auraState: AuraManagerState,
  baseCost: number,
  seatIndex: number,
  ft: FacilityType,
  previewDiscount = false,
): number {
  assertFiniteInt(baseCost, "getEffectiveCost baseCost");
  assertFiniteInt(seatIndex, "getEffectiveCost seatIndex");

  const { afterStack } = stackAurasAndBumps(peqState, auraState, baseCost, seatIndex, ft);
  const cat = getCategory(ft);

  if (previewDiscount && hasAnyDiscount(peqState, seatIndex, cat, ft)) {
    // Find best-case discount (mirrors Unity preview logic)
    let best = afterStack;
    for (const pe of peekIncludingGlobal(peqState, seatIndex)) {
      if (!categoryFits(cat, pe.categoryFilter)) continue;
      if (!facilityFits(ft, pe.facilityFilter)) continue;
      if (pe.kind === "NextBuildFree" || pe.kind === "NextSolarFree") {
        best = 0;
      } else if (pe.kind === "NextBuildDiscount" || pe.kind === "NextNuclearDiscount") {
        best = Math.max(0, best - pe.valueInt);
      }
    }
    return best;
  }

  return afterStack;
}

// ---------------------------------------------------------------------------
// getEffectiveCostWithOverride (PendingKind? overload)
// ---------------------------------------------------------------------------

/**
 * Quote with a specific discount kind override.
 * Validates that a matching queued PEQ entry exists before applying discount.
 * Mirrors BuildCostService.GetEffectiveCost(seatIndex, ft, terrain, era, PendingKind?).
 *
 * Codex grill A4 fix: ALL discount kinds must verify a queued entry matches
 * before applying — closes Solar-free-on-Coal exploit.
 */
export function getEffectiveCostWithOverride(
  peqState: PendingEffectQueueState,
  auraState: AuraManagerState,
  baseCost: number,
  seatIndex: number,
  ft: FacilityType,
  overrideDiscount: PendingKind | null,
): number {
  assertFiniteInt(baseCost, "getEffectiveCostWithOverride baseCost");
  assertFiniteInt(seatIndex, "getEffectiveCostWithOverride seatIndex");

  const afterStack = getEffectiveCost(peqState, auraState, baseCost, seatIndex, ft, false);
  if (overrideDiscount == null) return afterStack;

  const cat = getCategory(ft);

  switch (overrideDiscount) {
    case "NextBuildFree":
    case "NextSolarFree": {
      for (const pe of peekIncludingGlobal(peqState, seatIndex)) {
        if (pe.kind !== overrideDiscount) continue;
        if (!categoryFits(cat, pe.categoryFilter)) continue;
        if (!facilityFits(ft, pe.facilityFilter)) continue;
        return 0;
      }
      return afterStack;
    }
    case "NextBuildDiscount":
    case "NextNuclearDiscount": {
      let reduce = 0;
      for (const pe of peekIncludingGlobal(peqState, seatIndex)) {
        if (pe.kind !== overrideDiscount) continue;
        if (!categoryFits(cat, pe.categoryFilter)) continue;
        if (!facilityFits(ft, pe.facilityFilter)) continue;
        reduce = pe.valueInt;
        break;
      }
      return Math.max(0, afterStack - reduce);
    }
    case "NextPaidDrawFree":
    case "NextBuildCostBump":
    case "HalveNextTokenLoss":
      // Not build-side discounts; return stacking-only quote.
      console.warn(
        `[BuildCostService] overrideDiscount=${overrideDiscount} is not a build-side discount kind; ignoring.`,
      );
      return afterStack;
    default: {
      const _exhaustive: never = overrideDiscount;
      throw new RangeError(`[BuildCostService] Unhandled PendingKind: ${String(_exhaustive)}`);
    }
  }
}

// ---------------------------------------------------------------------------
// getEffectiveCostFromHand (hand-driven path)
// ---------------------------------------------------------------------------

/**
 * Hand-aware effective-cost. Scans hand for bumps (auto-apply) + applies
 * chosen discount card instance if any.
 *
 * Recomputes HandDiscount rows internally from current CIS state — single source
 * of truth, prevents stale or tampered caller-supplied rows.
 *
 * chosenInstanceId = -1 → no discount, only bumps apply.
 * chosenInstanceId ≥ 0  → validate + apply chosen hand card discount.
 *
 * Mirrors BuildCostService.GetEffectiveCostFromHand.
 */
export function getEffectiveCostFromHand(
  cisState: CardInstanceState,
  cards: ReadonlyArray<CardDef>,
  auraState: AuraManagerState,
  baseCost: number,
  seatIndex: number,
  ft: FacilityType,
  chosenInstanceId: number,
): number {
  assertFiniteInt(baseCost, "getEffectiveCostFromHand baseCost");
  assertFiniteInt(seatIndex, "getEffectiveCostFromHand seatIndex");
  assertFiniteInt(chosenInstanceId, "getEffectiveCostFromHand chosenInstanceId");

  // Recompute internally — single source of truth, no stale/tampered rows.
  const handDiscounts = applicableHandDiscounts(cisState, cards, seatIndex, ft);

  const cat = getCategory(ft);

  // Sum bumps from hand (auto-apply)
  let bumps = 0;
  for (const d of handDiscounts) {
    if (d.kind !== "NextBuildCostBump") continue;
    if (d.categoryFilter != null && d.categoryFilter !== cat) continue;
    bumps += d.valueInt;
  }

  // Apply BuildCost auras
  let multAcc = 1;
  let addAcc = bumps;
  for (const a of getByScope(auraState, "BuildCost", seatIndex)) {
    // WEB.4.1: honor facilityFilter (FacilityType-level) + categoryFilter.
    if (a.categoryFilter != null && a.categoryFilter !== cat) continue;
    if (a.facilityFilter != null && a.facilityFilter !== ft) continue;
    // WEB.4.1: mult=0 semantic fix (option c).
    const eMult = a.effect.mult !== undefined ? a.effect.mult : 1;
    const eAdd = a.effect.add ?? 0;
    multAcc *= eMult;
    addAcc += eAdd;
  }
  const { result: afterStack } = applyModifier(baseCost, multAcc, addAcc, "BuildCost");

  // Apply chosen discount if valid
  if (chosenInstanceId < 0) return afterStack;

  for (const d of handDiscounts) {
    if (d.instanceId !== chosenInstanceId) continue;
    // Applicability validation (filter match)
    if (d.categoryFilter != null && d.categoryFilter !== cat) return afterStack;
    if (d.facilityFilter != null && d.facilityFilter !== ft) return afterStack;
    // Whitelist: only build-side discount kinds
    switch (d.kind) {
      case "NextBuildFree":
      case "NextSolarFree":
        return 0;
      case "NextBuildDiscount":
      case "NextNuclearDiscount":
        return Math.max(0, afterStack - d.valueInt);
      default:
        return afterStack; // chosen card isn't a build discount
    }
  }
  return afterStack; // chosenInstanceId not found in hand
}

// ---------------------------------------------------------------------------
// onBuildExecuted (PEQ-path execution)
// ---------------------------------------------------------------------------

/**
 * Drain pending bumps + optionally consume the chosen discount from PEQ.
 * Call AFTER TrySpend succeeds.
 *
 * chosenDiscountKind = null → bumps drain only.
 *
 * Mirrors BuildCostService.OnBuildExecuted(seatIndex, ft, chosenDiscountKind?).
 */
export function onBuildExecuted(
  peqState: PendingEffectQueueState,
  log: ActionLog,
  seatIndex: number,
  ft: FacilityType,
  chosenDiscountKind: PendingKind | null = null,
): BuildExecuteResult {
  assertFiniteInt(seatIndex, "onBuildExecuted seatIndex");

  const cat = getCategory(ft);

  // Drain bumps. Codex WEB.4.D R1 HIGH fix: thread `ft` so type-filtered
  // bumps (Drought Hydro) only drain on the matching FacilityType build.
  const {
    newState: stateAfterDrain,
    log: logAfterDrain,
    count,
  } = drainBumps(peqState, log, seatIndex, cat, ft);

  let currentState = stateAfterDrain;
  let currentLog = logAfterDrain;

  if (count > 0) {
    currentLog = currentLog.append({
      kind: "build.bumps.drained",
      data: { seat: seatIndex, ft, drained: count } satisfies JsonValue,
    });
  }

  // Consume chosen discount
  if (chosenDiscountKind != null) {
    const {
      newState: stateAfterConsume,
      log: logAfterConsume,
      ok,
    } = tryConsume(currentState, currentLog, seatIndex, chosenDiscountKind, cat, ft);
    if (!ok) {
      console.warn(
        `[BuildCostService] onBuildExecuted: chosen discount ${chosenDiscountKind} not present for seat ${seatIndex} cat ${cat} ft ${ft}.`,
      );
    }
    currentState = stateAfterConsume;
    currentLog = logAfterConsume;
  }

  return { newState: currentState, log: currentLog };
}

// ---------------------------------------------------------------------------
// onBuildExecutedFromHand (hand-driven execution)
// ---------------------------------------------------------------------------

/**
 * Hand-aware build execution. Discards consumed bump cards + chosen discount
 * card from hand (moves zone to Discard in CardInstanceState).
 *
 * Recomputes HandDiscount rows internally from current CIS state — single source
 * of truth, prevents stale or tampered caller-supplied rows from moving wrong cards.
 *
 * Caller must have already TrySpend'd for the cost from getEffectiveCostFromHand.
 *
 * Mirrors BuildCostService.OnBuildExecutedFromHand(seatIndex, ft, chosenInstanceId).
 */
export function onBuildExecutedFromHand(
  cisState: CardInstanceState,
  cards: ReadonlyArray<CardDef>,
  log: ActionLog,
  seatIndex: number,
  ft: FacilityType,
  chosenInstanceId = -1,
): BuildExecuteFromHandResult {
  assertFiniteInt(seatIndex, "onBuildExecutedFromHand seatIndex");
  assertFiniteInt(chosenInstanceId, "onBuildExecutedFromHand chosenInstanceId");

  // Recompute internally — single source of truth, no stale/tampered rows.
  const handDiscounts = scanHandForBuildDiscounts(cisState, cards, seatIndex);

  const cat = getCategory(ft);

  let currentState = cisState;
  let currentLog = log;
  let bumpsDrained = 0;

  // Drain bump cards (auto-apply matching category)
  for (const d of handDiscounts) {
    if (d.kind !== "NextBuildCostBump") continue;
    if (d.categoryFilter != null && d.categoryFilter !== cat) continue;
    const { newState, log: nextLog } = moveZone(currentState, currentLog, d.instanceId, "Discard");
    currentState = newState;
    currentLog = nextLog;
    bumpsDrained++;
  }

  if (bumpsDrained > 0) {
    currentLog = currentLog.append({
      kind: "build.bumps.drained",
      data: { seat: seatIndex, ft, drained: bumpsDrained } satisfies JsonValue,
    });
  }

  // Validate + whitelist chosen discount before discard
  if (chosenInstanceId >= 0) {
    let found = false;
    for (const d of handDiscounts) {
      if (d.instanceId !== chosenInstanceId) continue;
      if (d.categoryFilter != null && d.categoryFilter !== cat) break;
      if (d.facilityFilter != null && d.facilityFilter !== ft) break;

      switch (d.kind) {
        case "NextBuildFree":
        case "NextSolarFree":
        case "NextBuildDiscount":
        case "NextNuclearDiscount": {
          const { newState, log: nextLog } = moveZone(
            currentState,
            currentLog,
            d.instanceId,
            "Discard",
          );
          currentState = newState;
          currentLog = nextLog;
          found = true;
          break;
        }
        default:
          console.error(
            `[BuildCostService.onBuildExecutedFromHand] chosenInstanceId=${chosenInstanceId} has non-build kind ${d.kind}; refusing discard.`,
          );
          break;
      }
      break;
    }

    if (!found) {
      console.warn(
        `[BuildCostService.onBuildExecutedFromHand] chosenInstanceId=${chosenInstanceId} not applicable build-side for ${ft} (silent skip).`,
      );
    }
  }

  return { newState: currentState, log: currentLog };
}
